import React, {useEffect, useState} from 'react';
import {QRCodeSVG} from 'qrcode.react';
import {ShieldIcon} from '../../components/icons';
import RecoveryCodesModal from '../../components/RecoveryCodesModal';
import * as twoFactor from '../../service/TwoFactorService';
import {getConfig} from '../../support/Config';

/**
 * Безопасность аккаунта. Двухфакторная аутентификация настраивается прямо здесь
 * (локальный TOTP), смена пароля по-прежнему уходит в кабинет Keycloak.
 */
const ProfileSecurityPage: React.FC = () => {
    const [enabled, setEnabled] = useState<boolean | null>(null);
    const [setupData, setSetupData] = useState<twoFactor.TwoFactorSetup | null>(null);
    const [code, setCode] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
    const [recoveryRemaining, setRecoveryRemaining] = useState(0);

    useEffect(() => {
        twoFactor.getStatus()
            .then(status => {
                setEnabled(status.enabled);
                setRecoveryRemaining(status.recoveryCodesRemaining || 0);
            })
            .catch(() => setEnabled(false));
    }, []);

    const startSetup = async () => {
        setBusy(true);
        setError('');
        try {
            setSetupData(await twoFactor.setup());
            setCode('');
        } catch {
            setError('Не удалось начать подключение. Попробуйте позже.');
        } finally {
            setBusy(false);
        }
    };

    const cancelSetup = () => {
        setSetupData(null);
        setCode('');
        setError('');
    };

    const confirmEnable = async (e: React.FormEvent) => {
        e.preventDefault();
        setBusy(true);
        setError('');
        try {
            const codes = await twoFactor.enable(code.trim());
            setEnabled(true);
            setSetupData(null);
            setCode('');
            setRecoveryCodes(codes);
            setRecoveryRemaining(codes.length);
        } catch {
            setError('Неверный код. Проверьте код в приложении и попробуйте снова.');
        } finally {
            setBusy(false);
        }
    };

    const confirmDisable = async (e: React.FormEvent) => {
        e.preventDefault();
        setBusy(true);
        setError('');
        try {
            await twoFactor.disable(code.trim());
            setEnabled(false);
            setCode('');
            setRecoveryRemaining(0);
        } catch {
            setError('Неверный код. Введите код из приложения или recovery-код.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="card p-6">
                <div className="flex items-start gap-4">
                    <span className="w-11 h-11 rounded-xl bg-ink-900 text-white dark:bg-white dark:text-ink-900 flex items-center justify-center flex-shrink-0">
                        <ShieldIcon className="w-6 h-6"/>
                    </span>
                    <div className="flex-1 min-w-0">
                        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                            Двухфакторная аутентификация
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            Одноразовый код из приложения-аутентификатора при входе (TOTP).
                        </p>
                    </div>
                    {enabled !== null && (
                        <span className="chip flex-shrink-0" data-testid="tfa-status">
                            {enabled ? 'Включена' : 'Выключена'}
                        </span>
                    )}
                </div>

                {error && <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">{error}</p>}
                {enabled === null && <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">Загрузка…</p>}

                {enabled === false && !setupData && (
                    <button onClick={startSetup} disabled={busy} className="btn-primary mt-6 min-h-[44px]">
                        Включить 2FA
                    </button>
                )}

                {enabled === false && setupData && (
                    <div className="mt-5 space-y-4">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            1. Отсканируйте QR-код в приложении (Google Authenticator, Authy и другие):
                        </p>
                        <div className="inline-block p-4 bg-white rounded-xl border border-gray-200">
                            <QRCodeSVG value={setupData.otpauthUri} size={168}/>
                        </div>
                        <div>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Или введите ключ вручную:</p>
                            <code className="block break-all text-sm font-mono p-3 bg-gray-50 dark:bg-ink-900 rounded-xl text-gray-900 dark:text-gray-100">
                                {setupData.secret}
                            </code>
                        </div>
                        <form onSubmit={confirmEnable} className="space-y-3">
                            <label htmlFor="tfa-enable-code" className="block text-sm text-gray-600 dark:text-gray-400">
                                2. Введите код из приложения для подтверждения:
                            </label>
                            <input
                                id="tfa-enable-code"
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                maxLength={8}
                                value={code}
                                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                                placeholder="123456"
                                className="input-field max-w-[220px] text-center text-xl tracking-[0.3em] font-mono"
                            />
                            <div className="flex gap-2">
                                <button type="submit" disabled={busy || code.length < 6}
                                        className="btn-primary min-h-[44px]">
                                    Подтвердить
                                </button>
                                <button type="button" onClick={cancelSetup} className="btn-secondary min-h-[44px]">
                                    Отмена
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {enabled === true && (
                    <div className="mt-5 space-y-4">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            Recovery-кодов осталось: <span className="font-medium text-gray-900 dark:text-gray-100"
                                                           data-testid="recovery-remaining">{recoveryRemaining}</span>
                        </p>
                        <form onSubmit={confirmDisable} className="space-y-3">
                            <label htmlFor="tfa-disable-code" className="block text-sm text-gray-600 dark:text-gray-400">
                                Чтобы отключить 2FA, введите код из приложения или recovery-код:
                            </label>
                            <input
                                id="tfa-disable-code"
                                type="text"
                                maxLength={10}
                                value={code}
                                onChange={(e) => setCode(e.target.value.replace(/[^a-zA-Z0-9-]/g, '').toUpperCase())}
                                placeholder="123456"
                                className="input-field max-w-[220px] text-center text-xl tracking-[0.3em] font-mono"
                            />
                            <button type="submit" disabled={busy || code.length < 6}
                                    className="btn-danger min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed">
                                Отключить 2FA
                            </button>
                        </form>
                    </div>
                )}

                {recoveryCodes && (
                    <RecoveryCodesModal codes={recoveryCodes} onClose={() => setRecoveryCodes(null)}/>
                )}
            </div>

            <div className="card p-6">
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2">Пароль</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Смена пароля выполняется через Keycloak. Нажмите кнопку ниже, чтобы перейти в раздел смены пароля.
                </p>
                <a
                    href={`${getConfig('keycloakUrl')}/realms/${getConfig('keycloakRealm')}/account/#/security/signingin`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-secondary inline-flex min-h-[44px]"
                >
                    Изменить пароль
                </a>
            </div>
        </div>
    );
};

export default ProfileSecurityPage;
